import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Result,
  Spin,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';

import ContentCard from '../../components/ContentCard';
import FieldSelect from '../../components/FieldSelect';
import OpenClassAPI from '../../openClasses/OpenClassAPI';
import { offeredCertificateType } from '../../openClasses/utils/openClass';
import {
  OpenClass,
  OpenClassBookingOrderDetails as OpenClassBookingOrderDetailsType,
} from '../../openClasses/types/OpenClass';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import useDebounce from '../../utils/useDebounce';
import { usePrompt } from '../../utils/usePrompt';
import ClientCompanyAPI from '../api/ClientCompanyAPI';
import CreateClientCompanyModal from '../components/CreateClientCompanyModal';
import CreateContactPersonModal from '../components/CreateContactPersonModal';
import OpenClassOrderParticipantTable from '../components/OpenClassOrderParticipantTable';
import OpenClassAttendeeCsvButtons from '../components/OpenClassAttendeeCsvButtons';
import OpenClassBulkAssignmentBar from '../components/OpenClassBulkAssignmentBar';
import { MinimalClientCompany } from '../types/ClientCompany';
import ContactPerson from '../types/ContactPerson';
import { getFullContactName } from '../utils/contactPerson';
import {
  describeOpenClassOrderError,
  getOpenClassBookingOrderPath,
  getOpenClassOrderSubsidiaryCompanyId,
} from '../utils/openClassBookingOrder';
import useContactPersonOptions from '../hooks/useContactPersonOptions';
import {
  ParticipantDraft,
  ParticipantSavePlan,
  applyAssignmentToDrafts,
  attendeesLosingSentStamp,
  buildParticipantSavePlan,
  changedParticipantKeys,
  draftHasInvalidBirthDate,
  draftMissesBirthDetails,
  draftsFromDetails,
  emptyParticipantDraft,
  isParticipantDraftValid,
  mergeFailedDrafts,
  participantDraftsEqual,
  registrationDraftToSelection,
  updateDropsFurboRequirement,
} from '../utils/openClassParticipantDraft';

type OpenClassBookingOrderDetailsProps = {
  isNewOrder?: boolean;
  isStandalone?: boolean;
  orderTypeSelector?: ReactNode;
};

type OrderFormValues = {
  clientCompanyId?: number;
  comment?: string;
  contactEmail?: string;
  contactName?: string;
  contactPersonId?: number;
  contactPhoneNumber?: string;
  orderDate: Dayjs;
};

const orderFormValues = (
  order: OpenClassBookingOrderDetailsType['order'],
): OrderFormValues => ({
  clientCompanyId: order.clientCompany?.id,
  comment: order.comment ?? undefined,
  contactPersonId: order.contactPerson?.id,
  contactName: order.contact?.name,
  contactEmail: order.contact?.email,
  contactPhoneNumber: order.contact?.phoneNumber ?? undefined,
  orderDate: dayjs(order.orderDate),
});

type AttendeeSaveResult = {
  failedRows: ParticipantDraft[];
  rowErrors: Record<string, string>;
  generalErrors: string[];
};

type AttendeeFailureNavigationState = Partial<AttendeeSaveResult> & {
  suggestedRows?: ParticipantDraft[];
};

const OpenClassBookingOrderDetails: React.FC<
  OpenClassBookingOrderDetailsProps
> = ({ isNewOrder = false, isStandalone = false, orderTypeSelector }) => {
  const [currentUser] = useUser();
  const { openClassBookingOrderId } = useParams();

  const orderId =
    openClassBookingOrderId && /^\d+$/.test(openClassBookingOrderId)
      ? Number(openClassBookingOrderId)
      : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [form] = Form.useForm<OrderFormValues>();
  const [details, setDetails] = useState<OpenClassBookingOrderDetailsType>();
  const [openClasses, setOpenClasses] = useState<OpenClass[]>([]);
  const [clientCompanies, setClientCompanies] = useState<
    MinimalClientCompany[]
  >([]);
  const [loading, setLoading] = useState(!isNewOrder);
  const [saving, setSaving] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [createClientCompanyOpen, setCreateClientCompanyOpen] = useState(false);
  const [createContactPersonOpen, setCreateContactPersonOpen] = useState(false);
  const [createdContactPerson, setCreatedContactPerson] =
    useState<ContactPerson>();
  const [selectedContactPerson, setSelectedContactPerson] =
    useState<ContactPerson | null>();
  const [drafts, setDrafts] = useState<ParticipantDraft[]>([]);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const navigationStateRef = useRef<AttendeeFailureNavigationState | null>(
    (location.state as AttendeeFailureNavigationState | null) ?? null,
  );

  const watchedClientCompanyId = Form.useWatch('clientCompanyId', form);
  const watchedContactPersonId = Form.useWatch('contactPersonId', form);
  const watchedContactName = Form.useWatch('contactName', form);
  const watchedContactEmail = Form.useWatch('contactEmail', form);
  const watchedOrderDate = Form.useWatch('orderDate', form);

  const order = details?.order;
  const originals = useMemo(() => draftsFromDetails(details), [details]);
  const attendeesDirty = useMemo(
    () => !participantDraftsEqual(drafts, originals),
    [drafts, originals],
  );
  const dirtyParticipantKeys = useMemo(
    () => changedParticipantKeys(drafts, originals),
    [drafts, originals],
  );
  const pageDirty = orderDirty || attendeesDirty;

  usePrompt(
    t('views.BookingOrderDetails.unsavedChanges'),
    pageDirty && !saving && !loading,
  );

  // Seat counts live on the classes, so any write that takes or frees one has to re-read them.
  const refreshOpenClasses = useCallback(async () => {
    try {
      const { data } = await OpenClassAPI.getOpenClasses();
      setOpenClasses(data);
    } catch {
      // Keeping the stale list beats emptying the pickers, and the backend still
      // rejects a full session.
    }
  }, []);

  const onDraftsChange = useCallback((nextDrafts: ParticipantDraft[]) => {
    const availableKeys = new Set(nextDrafts.map((draft) => draft.key));
    setSelectedKeys((keys) => keys.filter((key) => availableKeys.has(key)));
    setDrafts(nextDrafts);
  }, []);

  const canCreate = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_CREATE,
  );
  const canUpdate = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_UPDATE,
  );
  const canDelete = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_DELETE,
  );
  const canReadClientCompanies = userHasPermission(
    currentUser,
    Permission.CLIENT_COMPANY_READ_LIST,
  );
  const canReadContactPersons = userHasPermission(
    currentUser,
    Permission.CONTACT_PERSON_READ_LIST,
  );
  const canCreateClientCompanies = userHasPermission(
    currentUser,
    Permission.CLIENT_COMPANY_CREATE,
  );
  const canCreateContactPersons = userHasPermission(
    currentUser,
    Permission.CONTACT_PERSON_CREATE,
  );
  const isStandaloneOrder = isNewOrder
    ? isStandalone
    : order?.clientCompany === null;

  const canSave =
    (isNewOrder ? canCreate : canUpdate) &&
    (isStandaloneOrder || (canReadClientCompanies && canReadContactPersons));

  const orderSubsidiaryCompanyId = useMemo(
    () =>
      loading
        ? undefined
        : getOpenClassOrderSubsidiaryCompanyId(currentUser, {
            isNewOrder,
            order,
          }),
    [currentUser, isNewOrder, loading, order],
  );

  const selectedContactPersonOptions = useMemo(
    () => [order?.contactPerson, createdContactPerson, selectedContactPerson],
    [createdContactPerson, order?.contactPerson, selectedContactPerson],
  );
  const {
    contactPersons,
    hasMoreContactPersons,
    loadFirstContactPersonPage,
    loadMoreContactPersons,
    loadingContactPersonOptions,
    loadingContactPersons,
    searchContactPersons,
  } = useContactPersonOptions({
    enabled: canReadContactPersons && !isStandaloneOrder,
    selectedContactPersons: selectedContactPersonOptions,
  });

  const selectedDrafts = useMemo(
    () => drafts.filter((draft) => selectedKeys.includes(draft.key)),
    [drafts, selectedKeys],
  );

  const { debounce: searchClientCompanies, loading: loadingClientCompanies } =
    useDebounce(
      (search: string) => {
        if (!canReadClientCompanies || !orderSubsidiaryCompanyId) return;

        return ClientCompanyAPI.getClientCompanies({
          marketId: currentUser?.market.id,
          name: search || undefined,
          subsidiaryCompanyIds: [orderSubsidiaryCompanyId],
        });
      },
      (response) => setClientCompanies(response.data.data),
      {
        beforeDebounce: () => setClientCompanies([]),
        dependencies: [
          canReadClientCompanies,
          currentUser?.market.id,
          orderSubsidiaryCompanyId,
        ],
      },
    );

  const loadOrderPage = useCallback(async () => {
    setLoading(true);
    setNotFound(false);

    if (!isNewOrder && orderId === undefined) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    const bookingOrderId = orderId;

    try {
      const openClassesPromise = OpenClassAPI.getOpenClasses();

      if (isNewOrder) {
        const [openClassesResponse] = await Promise.all([openClassesPromise]);
        setOpenClasses(openClassesResponse.data);
        setSelectedContactPerson(undefined);
        form.setFieldsValue({ orderDate: dayjs() });
        setOrderDirty(false);
        return;
      }

      const [detailsResponse, openClassesResponse] = await Promise.all([
        OpenClassAPI.getBookingOrderDetails(bookingOrderId!),
        openClassesPromise,
      ]);
      const loadedOrder = detailsResponse.data.order;

      const navigationState = navigationStateRef.current;
      navigationStateRef.current = null;
      if (navigationState?.failedRows || navigationState?.suggestedRows) {
        navigate(location.pathname, { replace: true });
      }

      setDetails(detailsResponse.data);
      setSelectedContactPerson(loadedOrder.contactPerson);
      setDrafts([
        ...mergeFailedDrafts(
          draftsFromDetails(detailsResponse.data),
          navigationState?.failedRows ?? [],
        ),
        ...(navigationState?.suggestedRows ?? []),
      ]);
      setRowErrors(navigationState?.rowErrors ?? {});
      setGeneralErrors(navigationState?.generalErrors ?? []);
      setOpenClasses(openClassesResponse.data);
      form.setFieldsValue(orderFormValues(loadedOrder));
      setOrderDirty(false);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(t('views.OpenClasses.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [form, isNewOrder, location.pathname, navigate, orderId, t]);

  useEffect(() => {
    loadOrderPage();
  }, [loadOrderPage]);

  useEffect(() => {
    if (
      canReadClientCompanies &&
      orderSubsidiaryCompanyId &&
      !isStandaloneOrder
    ) {
      searchClientCompanies('');
    } else {
      setClientCompanies([]);
    }
  }, [
    canReadClientCompanies,
    currentUser?.market.id,
    isStandaloneOrder,
    orderSubsidiaryCompanyId,
    searchClientCompanies,
  ]);

  const runAttendeeSavePlan = useCallback(
    async (
      targetOrderId: number,
      plan: ParticipantSavePlan,
      currentDrafts: ParticipantDraft[],
      currentOriginals: ParticipantDraft[],
    ): Promise<AttendeeSaveResult> => {
      const failedRows: ParticipantDraft[] = [];
      const failedRowErrors: Record<string, string> = {};
      const failures: string[] = [];
      const describe = (error: unknown) =>
        describeOpenClassOrderError(error, t, 'views.OpenClasses.saveFailed');
      const draftByParticipantId = new Map(
        currentDrafts
          .filter((draft) => draft.participantId != null)
          .map((draft) => [draft.participantId!, draft]),
      );
      const failedParticipantIds = new Set<number>();
      const failRow = (draft: ParticipantDraft, error: unknown) => {
        failedRowErrors[draft.key] = describe(error);
        if (draft.participantId != null) {
          failedParticipantIds.add(draft.participantId);
        }
        if (!failedRows.includes(draft)) failedRows.push(draft);
      };

      await Promise.all(
        plan.removeParticipantIds.map(async (participantId) => {
          try {
            await OpenClassAPI.removeOrderParticipant(
              targetOrderId,
              participantId,
            );
          } catch (error) {
            const name =
              currentOriginals.find(
                (original) => original.participantId === participantId,
              )?.name ?? t('views.OpenClassBookingOrderDetails.anAttendee');
            failures.push(
              t('views.OpenClassBookingOrderDetails.attendeeRemoveFailed', {
                name,
                reason: describe(error),
              }),
            );
          }
        }),
      );

      for (const changeSet of plan.changeSets) {
        const draft = draftByParticipantId.get(changeSet.participantId);
        try {
          for (const registrationId of changeSet.remove) {
            await OpenClassAPI.removeParticipantRegistration(
              targetOrderId,
              changeSet.participantId,
              registrationId,
            );
          }
          for (const { registrationId, selection } of changeSet.update.filter(
            updateDropsFurboRequirement,
          )) {
            await OpenClassAPI.updateParticipantRegistration(
              targetOrderId,
              changeSet.participantId,
              registrationId,
              selection,
            );
          }
        } catch (error) {
          if (draft) failRow(draft, error);
        }
      }

      await Promise.all(
        plan.detailChanges.map(async (change) => {
          if (failedParticipantIds.has(change.participantId)) return;
          const draft = draftByParticipantId.get(change.participantId);
          try {
            await OpenClassAPI.updateOrderParticipant(
              targetOrderId,
              change.participantId,
              {
                name: change.name,
                email: change.email || null,
                birthDate: change.birthDate || null,
                birthPlace: change.birthPlace || null,
              },
            );
          } catch (error) {
            if (draft) failRow(draft, error);
          }
        }),
      );

      for (const changeSet of plan.changeSets) {
        if (failedParticipantIds.has(changeSet.participantId)) continue;
        const draft = draftByParticipantId.get(changeSet.participantId);
        try {
          for (const { registrationId, selection } of changeSet.update.filter(
            (update) => !updateDropsFurboRequirement(update),
          )) {
            await OpenClassAPI.updateParticipantRegistration(
              targetOrderId,
              changeSet.participantId,
              registrationId,
              selection,
            );
          }
          for (const selection of changeSet.create) {
            await OpenClassAPI.createParticipantRegistration(
              targetOrderId,
              changeSet.participantId,
              selection,
            );
          }
        } catch (error) {
          if (draft) failRow(draft, error);
        }
      }

      let knownParticipantIds = new Set(
        currentOriginals.map((original) => original.participantId!),
      );

      for (const row of plan.addRows) {
        try {
          const { data } = await OpenClassAPI.addOrderParticipant(
            targetOrderId,
            {
              attendee: {
                name: row.name.trim(),
                email: row.email.trim() || null,
                birthDate: row.birthDate.trim() || null,
                birthPlace: row.birthPlace.trim() || null,
              },
            },
          );
          const responseIds = data.participants.map(({ id }) => id);
          const newParticipantId = responseIds.find(
            (id) => !knownParticipantIds.has(id),
          );
          knownParticipantIds = new Set(responseIds);
          if (newParticipantId === undefined) {
            if (row.registrations.length > 0) {
              failures.push(
                t(
                  'views.OpenClassBookingOrderDetails.attendeeRegistrationsFailed',
                  { name: row.name.trim() },
                ),
              );
            }
            continue;
          }
          try {
            for (const registration of row.registrations) {
              await OpenClassAPI.createParticipantRegistration(
                targetOrderId,
                newParticipantId,
                registrationDraftToSelection(registration),
              );
            }
          } catch (error) {
            failRow(
              {
                ...row,
                key: `p-${newParticipantId}`,
                participantId: newParticipantId,
              },
              error,
            );
          }
        } catch (error) {
          failRow(row, error);
        }
      }

      return {
        failedRows,
        rowErrors: failedRowErrors,
        generalErrors: failures,
      };
    },
    [t],
  );

  const saveOrder = useCallback(
    async (values: OrderFormValues) => {
      if (!canSave) return;

      const invalidRowErrors: Record<string, string> = {};
      let invalidRowCount = 0;
      drafts.forEach((draft) => {
        if (draft.hasRecordedOutcome) return;
        if (!isParticipantDraftValid(draft)) {
          invalidRowCount += 1;
          if (!draft.name.trim()) {
            invalidRowErrors[draft.key] = t(
              'views.OpenClassBookingOrderDetails.attendeeNameRequired',
            );
          } else if (
            !draftMissesBirthDetails(draft) &&
            !draftHasInvalidBirthDate(draft)
          ) {
            invalidRowErrors[draft.key] = t(
              'views.OpenClassBookingOrderDetails.attendeeEmailInvalid',
            );
          }
        }
      });
      if (invalidRowCount > 0) {
        setRowErrors(invalidRowErrors);
        setGeneralErrors([]);
        message.warning(
          t('views.OpenClassBookingOrderDetails.attendeeRowsInvalid', {
            count: invalidRowCount,
          }),
        );
        return;
      }

      if (!isNewOrder && details) {
        const losingStamp = attendeesLosingSentStamp(
          buildParticipantSavePlan(drafts, originals),
          details.participants,
        );
        if (losingStamp.length > 0) {
          const confirmed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
              content: t(
                'views.OpenClassBookingOrderDetails.certificateSentClearedConfirm',
                { count: losingStamp.length, names: losingStamp.join(', ') },
              ),
              okButtonProps: { danger: true },
              title: t('views.OpenClasses.certificateType'),
              onCancel: () => resolve(false),
              onOk: () => resolve(true),
            });
          });
          if (!confirmed) return;
        }
      }

      const orderDate = values.orderDate.format('YYYY-MM-DD');
      const comment = values.comment?.trim() || null;
      const contact = isStandaloneOrder
        ? {
            name: values.contactName!.trim(),
            email: values.contactEmail!.trim(),
            phoneNumber: values.contactPhoneNumber?.trim() || null,
          }
        : undefined;
      const contactPersonId = isStandaloneOrder
        ? undefined
        : (values.contactPersonId ?? null);

      setSaving(true);
      try {
        if (isNewOrder) {
          const { data } = contact
            ? await OpenClassAPI.createStandaloneBookingOrder({
                comment,
                contact,
                orderDate,
                subsidiaryCompanyId: orderSubsidiaryCompanyId!,
              })
            : await OpenClassAPI.createBookingOrder({
                clientCompanyId: values.clientCompanyId!,
                comment,
                contactPersonId,
                orderDate,
              });
          const newOrderId = data.order.id;
          const result = await runAttendeeSavePlan(
            newOrderId,
            {
              removeParticipantIds: [],
              detailChanges: [],
              changeSets: [],
              addRows: drafts,
            },
            drafts,
            [],
          );
          const hasFailures =
            result.failedRows.length > 0 ||
            result.generalErrors.length > 0 ||
            Object.keys(result.rowErrors).length > 0;
          if (hasFailures) {
            message.warning(
              t(
                'views.OpenClassBookingOrderDetails.attendeesSaveFailuresTitle',
              ),
            );
          } else {
            message.success(t('views.OpenClassBookingOrderDetails.created'));
          }
          const suggestState =
            contact && drafts.length === 0
              ? {
                  state: {
                    suggestedRows: [
                      {
                        ...emptyParticipantDraft(),
                        name: contact.name,
                        email: contact.email,
                      },
                    ],
                  },
                }
              : undefined;
          navigate(
            getOpenClassBookingOrderPath(newOrderId),
            hasFailures ? { state: result } : suggestState,
          );
        } else if (orderId !== undefined) {
          const plan = buildParticipantSavePlan(drafts, originals);
          await OpenClassAPI.updateBookingOrder(orderId, {
            clientCompanyId: values.clientCompanyId,
            comment,
            contact,
            contactPersonId,
            orderDate,
          });
          const result = await runAttendeeSavePlan(
            orderId,
            plan,
            drafts,
            originals,
          );
          const { data: freshDetails } =
            await OpenClassAPI.getBookingOrderDetails(orderId);
          setDetails(freshDetails);
          setOrderDirty(false);
          setSelectedContactPerson(freshDetails.order.contactPerson);
          onDraftsChange(
            mergeFailedDrafts(
              draftsFromDetails(freshDetails),
              result.failedRows,
            ),
          );
          setRowErrors(result.rowErrors);
          setGeneralErrors(result.generalErrors);
          refreshOpenClasses();
          if (
            result.generalErrors.length > 0 ||
            Object.keys(result.rowErrors).length > 0
          ) {
            message.warning(
              t(
                'views.OpenClassBookingOrderDetails.attendeesSaveFailuresTitle',
              ),
            );
          } else {
            message.success(t('views.OpenClassBookingOrderDetails.updated'));
          }
        }
      } catch {
        message.error(t('views.OpenClasses.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [
      canSave,
      details,
      drafts,
      isNewOrder,
      isStandaloneOrder,
      navigate,
      onDraftsChange,
      orderId,
      orderSubsidiaryCompanyId,
      originals,
      refreshOpenClasses,
      runAttendeeSavePlan,
      t,
    ],
  );

  const companyLocked = (details?.participants.length ?? 0) > 0;
  const standaloneOrderIncomplete =
    !watchedContactName?.trim() ||
    !watchedContactEmail?.trim() ||
    !watchedOrderDate ||
    !orderSubsidiaryCompanyId;
  const companyOrderIncomplete = !watchedClientCompanyId || !watchedOrderDate;
  const createDisabled =
    isNewOrder &&
    (isStandaloneOrder ? standaloneOrderIncomplete : companyOrderIncomplete);

  const discardChanges = useCallback(() => {
    if (isNewOrder) {
      form.resetFields();
      form.setFieldsValue({ orderDate: dayjs() });
      onDraftsChange([]);
    } else if (details) {
      form.setFieldsValue(orderFormValues(details.order));
      setSelectedContactPerson(details.order.contactPerson);
      onDraftsChange(originals);
    }
    setOrderDirty(false);
    setRowErrors({});
    setGeneralErrors([]);
    setSelectedKeys([]);
  }, [details, form, isNewOrder, onDraftsChange, originals]);

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spin size="large" />
      </div>
    );
  }

  if (notFound) {
    return (
      <Result
        status="404"
        subTitle={t('views.OpenClassBookingOrderDetails.notFound')}
        extra={
          <Button onClick={() => navigate('/bestallningar')}>
            {t('common.goBack')}
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className="sm:flex sm:flex-row items-center pb-2">
        <button
          type="button"
          className="hover:underline cursor-pointer text-blueGrayDark bg-transparent border-0 p-0 font-[inherit]"
          onClick={() =>
            (location.state as { prevPage?: string } | null)?.prevPage
              ? navigate(-1)
              : navigate('/bestallningar')
          }>
          <ArrowLeftOutlined style={{ fontSize: 11 }} />
          <span className="pl-1 pt-0.5 text-xs">{t('common.goBack')}</span>
        </button>
      </div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <h2 className="m-0">
            {isNewOrder
              ? t('views.OpenClassBookingOrderDetails.createTitle')
              : t('views.OpenClassBookingOrderDetails.editTitle')}
          </h2>
          {isNewOrder && orderTypeSelector}
        </div>
        <div className="flex items-center gap-3">
          {pageDirty && !saving && (
            <span className="text-sm font-medium text-orange-700">
              {t('views.OpenClassBookingOrderDetails.unsavedChanges')}
            </span>
          )}
          {canSave && (
            <Button
              disabled={createDisabled || (!isNewOrder && !pageDirty)}
              loading={saving}
              type="primary"
              onClick={() => form.submit()}>
              {isNewOrder
                ? t('common.create')
                : t('views.OpenClassBookingOrderDetails.saveChanges')}
            </Button>
          )}
        </div>
      </div>

      <ContentCard>
        {isNewOrder && isStandaloneOrder && (
          <p className="mb-6 text-sm text-blueGrayDark">
            {t('views.OpenClassBookingOrderDetails.standaloneDescription')}
          </p>
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={saveOrder}
          onValuesChange={() => setOrderDirty(true)}>
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-4">
            {!isStandaloneOrder && (
              <FieldSelect
                className="w-full"
                createButtonText={t(
                  'components.ClientCompanyField.createNewCompany',
                )}
                disabled={!canSave || companyLocked}
                elements={clientCompanies.map((clientCompany) => ({
                  id: clientCompany.id,
                  primaryText: clientCompany.name,
                }))}
                formLabel={t('common.company')}
                formName="clientCompanyId"
                formRules={[{ required: true }]}
                initialValue={watchedClientCompanyId}
                loading={loadingClientCompanies}
                loadingOptions={loadingClientCompanies}
                placeholderText={t('components.ClientCompanyField.pickCompany')}
                onCreate={
                  canCreateClientCompanies && orderSubsidiaryCompanyId
                    ? () => setCreateClientCompanyOpen(true)
                    : undefined
                }
                onSearch={searchClientCompanies}
                onSelect={(clientCompanyId) => {
                  form.setFieldValue('clientCompanyId', clientCompanyId);
                  setOrderDirty(true);
                }}
              />
            )}
            {isStandaloneOrder ? (
              <>
                <Form.Item
                  label={t('common.name')}
                  name="contactName"
                  rules={[{ required: true, whitespace: true }]}>
                  <Input autoFocus={isNewOrder} disabled={!canSave} />
                </Form.Item>
                <Form.Item
                  label={t('common.email')}
                  name="contactEmail"
                  rules={[{ required: true, type: 'email' }]}>
                  <Input disabled={!canSave} />
                </Form.Item>
                <Form.Item
                  label={t('common.phoneNumber')}
                  name="contactPhoneNumber">
                  <Input disabled={!canSave} />
                </Form.Item>
              </>
            ) : (
              <FieldSelect
                className="w-full"
                createButtonText={t(
                  'components.CreateContactPersonModal.createNewContactPerson',
                )}
                compactSelection
                disabled={!canSave}
                elements={contactPersons.map((contactPerson) => ({
                  id: contactPerson.id,
                  primaryText: getFullContactName(contactPerson),
                  secondaryText: contactPerson.email,
                }))}
                formLabel={t('views.OpenClasses.contactPerson')}
                formName="contactPersonId"
                initialValue={watchedContactPersonId}
                loading={loadingContactPersons}
                loadingOptions={loadingContactPersonOptions}
                placeholderText={t(
                  'components.CreateCourseInstanceModal.selectContactPerson',
                )}
                hasMore={hasMoreContactPersons}
                onCreate={
                  canCreateContactPersons
                    ? () => setCreateContactPersonOpen(true)
                    : undefined
                }
                onDropdownOpen={() => loadFirstContactPersonPage('')}
                onLoadMore={loadMoreContactPersons}
                onSearch={searchContactPersons}
                onSelect={(contactPersonId) => {
                  setSelectedContactPerson(
                    contactPersons.find(
                      (contactPerson) => contactPerson.id === contactPersonId,
                    ),
                  );
                  form.setFieldValue('contactPersonId', contactPersonId);
                  setOrderDirty(true);
                }}
              />
            )}
            <Form.Item
              label={t('views.OpenClasses.orderDate')}
              name="orderDate"
              rules={[{ required: true }]}>
              <DatePicker className="w-full" disabled={!canSave} />
            </Form.Item>
            <Form.Item label={t('views.OpenClasses.comment')} name="comment">
              <Input disabled={!canSave} />
            </Form.Item>
          </div>
        </Form>

        <div className="mt-1 border-0 border-t border-solid border-gray-300 pt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-4">
            <h2 className="m-0 text-xl font-semibold">
              {t('views.OpenClassBookingOrderDetails.attendeesTitle', {
                count: drafts.length,
              })}
            </h2>
            <OpenClassAttendeeCsvButtons
              canCreate={canCreate}
              drafts={drafts}
              openClasses={openClasses}
              onImported={(imported, skippedCount) => {
                if (imported.length > 0) {
                  onDraftsChange([...drafts, ...imported]);
                  message.success(
                    t('views.OpenClassBookingOrderDetails.csvImported', {
                      count: imported.length,
                    }),
                  );
                }
                if (skippedCount > 0) {
                  message.warning(
                    t('views.OpenClassBookingOrderDetails.csvRowsSkipped', {
                      count: skippedCount,
                    }),
                  );
                }
              }}
            />
          </div>
          {generalErrors.length > 0 && (
            <Alert
              className="mb-3"
              message={t(
                'views.OpenClassBookingOrderDetails.attendeesSaveFailuresTitle',
              )}
              description={
                <ul className="m-0 pl-4">
                  {generalErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              }
              showIcon
              type="error"
            />
          )}
          {canCreate && (
            <OpenClassBulkAssignmentBar
              className="mb-3"
              openClasses={openClasses}
              selectedCount={selectedDrafts.length}
              onApply={(selection) => {
                const count = selectedDrafts.length;
                const certificateType = offeredCertificateType(
                  openClasses.find(({ id }) => id === selection.openClassId),
                  selection.certificateTemplateId,
                );
                if (!certificateType) return;
                setDrafts((current) =>
                  applyAssignmentToDrafts(
                    current,
                    selectedKeys,
                    selection,
                    certificateType,
                  ),
                );
                setSelectedKeys([]);
                message.success(
                  t('views.OpenClassBookingOrderDetails.bulkAssigned', {
                    count,
                  }),
                );
              }}
            />
          )}
          <OpenClassOrderParticipantTable
            canCreate={canCreate}
            canDelete={canDelete}
            canUpdate={canUpdate}
            drafts={drafts}
            dirtyKeys={dirtyParticipantKeys}
            onDraftsChange={onDraftsChange}
            onSelectedKeysChange={setSelectedKeys}
            openClasses={openClasses}
            rowErrors={rowErrors}
            selectedKeys={selectedKeys}
          />
        </div>
      </ContentCard>
      {canSave && pageDirty && (
        <>
          <div aria-hidden className="h-24" />
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 pb-4">
            <div className="container mx-auto px-8">
              <div className="pointer-events-auto flex flex-col gap-3 rounded-lg border border-l-4 border-gray-200 border-l-safeLifeMedium bg-blueGrayLight px-4 py-3 shadow-xl sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-safeLifeMedium" />
                  <div>
                    <div className="font-semibold text-grayDark">
                      {t('views.OpenClassBookingOrderDetails.unsavedChanges')}
                    </div>
                    <div className="text-sm text-blueGrayDark">
                      {t(
                        'views.OpenClassBookingOrderDetails.unsavedChangesHelp',
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 justify-end gap-2">
                  <Button disabled={saving} onClick={discardChanges}>
                    {t('views.OpenClassBookingOrderDetails.discardChanges')}
                  </Button>
                  <Button
                    disabled={createDisabled}
                    loading={saving}
                    type="primary"
                    onClick={() => form.submit()}>
                    {isNewOrder
                      ? t('common.create')
                      : t('views.OpenClassBookingOrderDetails.saveChanges')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {orderSubsidiaryCompanyId && (
        <CreateClientCompanyModal
          subsidiaryCompanyId={orderSubsidiaryCompanyId}
          visible={createClientCompanyOpen}
          onCancel={() => setCreateClientCompanyOpen(false)}
          onCreate={(clientCompany) => {
            setClientCompanies((companies) => [clientCompany, ...companies]);
            form.setFieldValue('clientCompanyId', clientCompany.id);
            setOrderDirty(true);
            setCreateClientCompanyOpen(false);
          }}
        />
      )}
      <CreateContactPersonModal
        visible={createContactPersonOpen}
        onCancel={() => setCreateContactPersonOpen(false)}
        onCreate={(contactPerson) => {
          setCreatedContactPerson(contactPerson);
          setSelectedContactPerson(contactPerson);
          form.setFieldValue('contactPersonId', contactPerson.id);
          setOrderDirty(true);
          setCreateContactPersonOpen(false);
        }}
      />
    </>
  );
};

export default OpenClassBookingOrderDetails;
